import { useEffect, useRef } from 'react';
import { isLandscape } from '@/utils/device';

interface LandscapeTransitionHandlers {
  /** Fires on the portrait -> landscape transition (not while staying landscape). */
  onEnter?: () => void;
  /** Fires on the landscape -> portrait transition (not while staying portrait). */
  onExit?: () => void;
}

/**
 * Runs a callback when the viewport crosses the landscape boundary. Tracks the
 * previous orientation in a ref so handlers fire ONCE per transition rather
 * than on every resize, which lets callers respect manual state changes made
 * while an orientation is already active (e.g. a user expand in landscape).
 *
 * Listens to both `resize` and `orientationchange` because mobile browsers are
 * inconsistent about which one they emit on rotation.
 */
export const useLandscapeTransition = ({ onEnter, onExit }: LandscapeTransitionHandlers): void => {
  const wasLandscapeRef = useRef(isLandscape());
  // Keep the latest handlers in a ref so re-renders that pass new closures
  // don't re-subscribe the listeners on every render.
  const handlersRef = useRef<LandscapeTransitionHandlers>({ onEnter, onExit });
  handlersRef.current = { onEnter, onExit };

  useEffect(() => {
    const handleOrientationChange = () => {
      const nowLandscape = isLandscape();
      if (nowLandscape === wasLandscapeRef.current) return;
      wasLandscapeRef.current = nowLandscape;
      if (nowLandscape) handlersRef.current.onEnter?.();
      else handlersRef.current.onExit?.();
    };
    window.addEventListener('resize', handleOrientationChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('resize', handleOrientationChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);
};
